import { Divider, Flex, Skeleton } from "@mantine/core";
import type { JSX } from "react";
import type { DashboardResponse } from "./calls/getDashboardData";
import { useDashboardData } from "./calls/useDashboardData";
import { UpdatedTime } from "./components/UpdatedTime";
import { WidgetRow } from "./components/WidgetRow";
import { BarGraph, DropsIcon, IncreaseIcon } from "./icons";
import classes from "./PayWidget.module.css";

export const calculatePayOnNextEnroll = (data: DashboardResponse): number => {
    const netSales = data.enrolls - data.churns;
    if (netSales >= 0 && netSales + 1 < data.sellPayScale.length) {
        return data.sellPayScale[netSales + 1].pay - data.sellPayScale[netSales].pay;
    }
    if (netSales > 0 || netSales + 1 > data.sellPayScale.length) {
        return (
            data.sellPayScale[data.sellPayScale.length - 1].pay - data.sellPayScale[data.sellPayScale.length - 2].pay
        );
    }
    return 0;
};

export function PayWidget(): JSX.Element {
    const { data, isLoading } = useDashboardData();

    if (isLoading) {
        return (
            <>
                <Flex align="center" direction="column" justify="center" h="111px" data-testid="pay-widget-skeleton">
                    <Skeleton mt={10} h="16px" />
                    <Skeleton h="50px" mt={10} />
                    <Skeleton h="14px" mt={10} />
                </Flex>
            </>
        );
    }

    return (
        <>
            {data ? (
                <>
                    <Flex pt="xs" data-testid="pay-widget-display">
                        <WidgetRow
                            largeText={`₱${Math.trunc(data.sellPay + data.servePay)}`}
                            smallText=" projected earnings"
                        />
                    </Flex>
                    <Flex direction="column" pt="xs">
                        <WidgetRow
                            largeText={`₱${Math.trunc(calculatePayOnNextEnroll(data)).toString()}`}
                            smallText=" increase on next enroll"
                            SVGIcon={IncreaseIcon}
                        />
                        <WidgetRow
                            largeText={`₱${Math.trunc(data.sellRate).toString()}`}
                            smallText=" per VHDP enroll"
                            SVGIcon={BarGraph}
                        />
                        <WidgetRow
                            largeText={Math.trunc(data.churns).toString()}
                            smallText=" cancels"
                            SVGIcon={DropsIcon}
                        />
                    </Flex>
                    <Flex pt="xs" c="dark.2">
                        <UpdatedTime updatedTime={data?.lastUpdated} />
                    </Flex>
                </>
            ) : (
                <>
                    <Divider color="dark.4" />

                    <Flex
                        direction="column"
                        className={classes.main}
                        align="center"
                        justify="center"
                        p={0}
                        mt="xxs"
                        fw={400}
                        c="dark.2"
                        fz="xs"
                    >
                        Waiting on sales and survey data.
                    </Flex>
                </>
            )}
        </>
    );
}
